import Text from '@/components/Text';
import {
    flattenZodUnionErrors,
    formatZodErrors,
    resolveHandlebarsAsJson,
    TemplateSchema,
    tryCatch
} from '@squaredup/utilities';
import Field from 'components/forms/field/Field';
import DisplayJsonUi from 'components/forms/jsonForms/DisplayJsonUi';
import type { ResolvedUIConfig } from 'components/forms/jsonForms/resolveAutocompleteOptions';
import stringify from 'fast-json-stable-stringify';
import { omit } from 'lodash';
import React, { useEffect } from 'react';
import { FormProvider, useForm, useFormContext, type UseFormClearErrors, type UseFormSetError } from 'react-hook-form';
import type { PluginSaveFormData } from './SaveAsPluginModalContent';

const updateUiConfigValidation = (
    isValid: boolean,
    setError: UseFormSetError<PluginSaveFormData>,
    clearErrors: UseFormClearErrors<PluginSaveFormData>
) => {
    if (!isValid) {
        setError('uiConfig', {
            type: 'manual',
            message: 'Invalid JSON, see preview for details.'
        });
    } else {
        clearErrors('uiConfig');
    }
};

export const ConfigurationTab: React.FC<{
    rawUiConfig: unknown;
    uiConfigMapping: Record<string, unknown>;
    basePluginDataSourceConfig: object;
}> = ({ rawUiConfig, uiConfigMapping, basePluginDataSourceConfig }) => {
    const mainForm = useFormContext<PluginSaveFormData>();
    const previewForm = useForm();

    const uiConfigParse = TemplateSchema.safeParse(rawUiConfig);
    useEffect(() => {
        updateUiConfigValidation(uiConfigParse.success, mainForm.setError, mainForm.clearErrors);
    }, [mainForm.clearErrors, mainForm.setError, uiConfigParse.success]);

    const uiConfig = uiConfigParse.success ? (uiConfigParse.data as ResolvedUIConfig[]) : undefined;

    // Some properties are not relevant here, and importConfig in particular is massive
    const irrelevantDataSourceConfigFields = ['importConfig', 'lambdaName', 'pluginId', 'version'];

    const mappedDataSourceConfig =
        uiConfigMapping != null
            ? tryCatch(
                  () => ({
                      ...omit(basePluginDataSourceConfig, irrelevantDataSourceConfigFields),
                      ...JSON.parse(
                          resolveHandlebarsAsJson(stringify(uiConfigMapping), previewForm.watch(), '{}', false)
                      )
                  }),
                  () => undefined
              )()
            : undefined;

    return (
        <div className='flex flex-col gap-6 p-6'>
            <Text.H4 className='inline'>UI Config</Text.H4>

            <div className='grid grid-cols-2 grid-rows-2 gap-6'>
                <Field.Input
                    name='uiConfig'
                    defaultValue={[]}
                    label='UI.json template'
                    placeholder='Enter UI configuration JSON'
                    type='code'
                    heightConstraints={{ min: 400 }}
                    fieldClassName='col-start-1 row-start-1 mt-0'
                />

                <Field.Input
                    name='uiConfigMapping'
                    defaultValue={omit(basePluginDataSourceConfig, irrelevantDataSourceConfigFields)}
                    label='Map UI.json to dataSourceConfig'
                    placeholder='Enter UI configuration Mapping JSON'
                    type='code'
                    heightConstraints={{ min: 400 }}
                    fieldClassName='col-start-1 row-start-2 mt-0'
                />

                <FormProvider {...previewForm}>
                    <div className='col-start-2 row-start-1'>
                        <Text.H4>Preview</Text.H4>
                        <div className='p-6 px-20 border rounded-lg border-g max-h-[30rem] ray-500 scroll-overflow col-start-2 row-start-1'>
                            {uiConfig == null || uiConfig.length === 0 || !uiConfigParse.success ? (
                                uiConfigParse.success ? (
                                    <span className='text-gray-300'>No UI.json template.</span>
                                ) : (
                                    <pre className='break-all whitespace-pre-wrap text-statusErrorPrimary'>
                                        {JSON.stringify(
                                            flattenZodUnionErrors(uiConfigParse.error).map(formatZodErrors),
                                            null,
                                            4
                                        )}
                                    </pre>
                                )
                            ) : (
                                <DisplayJsonUi includeDisplayNameField={false} formFields={uiConfig} />
                            )}
                        </div>
                    </div>

                    <div className='col-start-2 row-start-2'>
                        <Text.H4>Output (Mapped Configuration)</Text.H4>
                        <div className='p-6 px-20 border border-gray-500 max-h-[30rem] rounded-lg scroll-overflow'>
                            {!uiConfigParse.success ? (
                                <span className='text-gray-300'>
                                    No UI configuration mapping, or mapping is invalid.
                                </span>
                            ) : (
                                <pre className='break-all whitespace-pre-wrap'>
                                    {JSON.stringify(mappedDataSourceConfig, null, 4)}
                                </pre>
                            )}
                        </div>
                    </div>
                </FormProvider>
            </div>
        </div>
    );
};
